import { CONFIG } from '../config.js';

/** @typedef {'low' | 'medium' | 'high'} QualityPreset */

/**
 * Runtime UI settings — maps menu controls to GraphicsSettings + game systems.
 */
export class UiSettingsController {
  /** @param {{ graphicsSettings: import('../rendering/GraphicsSettings.js').GraphicsSettings }} deps */
  constructor({ graphicsSettings }) {
    this.graphicsSettings = graphicsSettings;

    const ui = CONFIG.ui;
    /** @type {QualityPreset} */
    this.quality = ui.defaultQuality;
    this.bloom = CONFIG.graphics.bloom;
    this.digitalRain = CONFIG.rain.enabled;
    this.postProcessing = ui.defaultPostProcessing;
    /** @type {number} */
    this.npcDensity = ui.defaultNpcDensity;
    this.syncGraphicsSettings();
  }

  /** @returns {import('./UiSettingsController.js').UiSettingsSnapshot} */
  snapshot() {
    return {
      quality: this.quality,
      bloom: this.bloom,
      digitalRain: this.digitalRain,
      postProcessing: this.postProcessing,
      npcDensity: this.npcDensity,
    };
  }

  /** @param {Partial<import('./UiSettingsController.js').UiSettingsSnapshot>} patch */
  applyPatch(patch) {
    if (patch.quality !== undefined) this.quality = patch.quality;
    if (patch.bloom !== undefined) this.bloom = patch.bloom;
    if (patch.digitalRain !== undefined) this.digitalRain = patch.digitalRain;
    if (patch.postProcessing !== undefined) {
      this.postProcessing = patch.postProcessing;
    }
    if (patch.npcDensity !== undefined) this.npcDensity = patch.npcDensity;
  }

  /** Push values into GraphicsSettings (does not touch renderer / NPCs). */
  syncGraphicsSettings() {
    const gs = this.graphicsSettings;
    gs.rainEnabled = this.digitalRain;
    gs.setPostProcessingEnabled(this.postProcessing);
    gs.bloom = this.bloom;

    const tier = QUALITY_PRESETS[this.quality];
    gs.qualityPreset = this.quality;
    gs.renderDistance = tier.renderDistance;
    gs.pixelRatioMax = tier.pixelRatioMax;
    CONFIG.graphics.staticBinaryGlyphs = tier.staticBinaryGlyphs;
    if (this.quality === 'low') {
      gs.bloom = false;
      this.bloom = false;
    }
  }

  /** @returns {number} */
  getPixelRatioCap() {
    return QUALITY_PRESETS[this.quality].pixelRatioMax;
  }
}

/** @typedef {{ quality: QualityPreset; bloom: boolean; digitalRain: boolean; postProcessing: boolean; npcDensity: number }} UiSettingsSnapshot */

const QUALITY_PRESETS = {
  low: {
    renderDistance: 90,
    pixelRatioMax: 1,
    staticBinaryGlyphs: true,
  },
  medium: {
    renderDistance: CONFIG.streaming?.renderDistance ?? 150,
    pixelRatioMax: CONFIG.renderer.pixelRatioMax,
    staticBinaryGlyphs: CONFIG.graphics.staticBinaryGlyphs,
  },
  high: {
    renderDistance: 180,
    pixelRatioMax: Math.min(1.5, CONFIG.renderer.pixelRatioMax + 0.25),
    staticBinaryGlyphs: false,
  },
};
